
import { Component, Vue, Watch } from "vue-property-decorator";
import vNavigation from "@/components/vNavigation.vue";
import vHeader from "@/components/vHeader.vue";
import vFooter from "@/components/vFooter.vue";
import { LOGOUT_USER } from "@/store/ActionTypes";
import { mapState } from "vuex";
import VTooltip from "@/components/v-tooltip";
import { AddOnType } from "@/models/Subscriptions/AddOns/AddOnType";
import {AccountState} from "@/store/modules/AccountModule";
import {LocalizationState} from "@/store/modules/LocalizationModule";
import VUpsellModal from "@/components/modals/marketing/vUpsellModal.vue";
import VModal from "@/components/vModal.vue";
import systemRepository from "@/utils/API/SystemRepository";
import { TRANSLATIONS_FETCH } from "@/store/ActionTypes";

type TutorialConfig = {
  title: string;
  heading: string;
  description: string;
  subtext: string;
  embedSrc: string;
  embedTitle: string;
};

const defaultTutorialConfig: TutorialConfig = {
  title: "",
  heading: "",
  description: "",
  subtext: "",
  embedSrc: "",
  embedTitle: "",
};

Vue.use(VTooltip, {
  defaultTemplate:
    "<div class='tooltip-vue' role='tooltip'><div class='tooltip-vue-inner'></div></div>",
  defaultInnerSelector: ".tooltip-vue-inner",
});

@Component({
  components: {
    VUpsellModal,
    VModal,
    vNavigation,
    vHeader,
    vFooter,
  },
  computed: {
    ...mapState({
      localizationState: (state: any) => state.LocalizationModule,
      accountState: (state: any) => state.AccountModule,
    }),
  },
})
class User extends Vue {
  navigationOpen = false;
  headerOpen = false;
  costingMenuOpen = false;
  localizationState: LocalizationState;
  accountState: AccountState;
  currentBuildId: string | null = null;
  bannerText: string = "";
  pollTimer: number | null = null;

  showTrialUpsellModal: boolean = false;
  trialUpsellMessage: string = "";
  showTutorialModal: boolean = false;
  showTutorialButton: boolean = false;
  isTutorialEmbedLoading: boolean = false;
  activeTutorial: TutorialConfig = { ...defaultTutorialConfig };

  openTrialUpsellModal(
    message: string = this.getDefaultTrialUpsellMessage(),
    tutorial: TutorialConfig | null = null
  ) {
    this.trialUpsellMessage = message;
    this.activeTutorial = tutorial ? { ...tutorial } : { ...defaultTutorialConfig };
    this.showTutorialButton = !!tutorial;
    this.showTrialUpsellModal = true;
  }
  closeTrialUpsellModal() {
    this.showTrialUpsellModal = false;
    this.trialUpsellMessage = this.getDefaultTrialUpsellMessage();
    this.showTutorialButton = false;
  }

  openTutorialModal() {
    if (!this.activeTutorial.embedSrc) {
      return;
    }

    this.isTutorialEmbedLoading = true;
    this.showTutorialModal = true;
  }

  closeTutorialModal() {
    this.showTutorialModal = false;
    this.isTutorialEmbedLoading = false;
  }

  handleTutorialIframeLoad() {
    this.isTutorialEmbedLoading = false;
  }

  closeMobile() {
    if (
      typeof window.orientation !== "undefined" ||
      navigator.userAgent.indexOf("IEMobile") !== -1
    ) {
      this.closeNavigation();
    }
  }

  get isCostingRouteActive() {
    return [
      "ingredient-cost",
      "additional-cost",
      "recipe-cost",
      "recipe-invoice",
    ].some((pathSegment) => this.$route.path.includes(pathSegment));
  }

  // Show recipe cost link, check if user paid for recipe cost
  showRecipeCostLink() {
    if (this.accountState.userDetails.addOns) {
      return this.accountState.userDetails.addOns
        .map((x: any) => x.addOn)
        .includes(AddOnType.RecipeCost);
    }
    return false;
  }

  showWellnessLink() {
    if (this.accountState.userDetails.isTrial) {
      return true;
    }

    if (this.accountState.userDetails.addOns) {
      return this.accountState.userDetails.addOns
        .map((x: any) => x.addOn)
        .includes(AddOnType.WellnessModule);
    }
    return false;
  }

  showAllergensAiLink() {
    if (this.accountState.userDetails.isTrial) {
      return true;
    }

    if (this.accountState.userDetails.addOns) {
      return this.accountState.userDetails.addOns
        .map((x: any) => x.addOn)
        .includes(AddOnType.AllergenAI);
    }
    return false;
  }

  logout() {
    this.$store.dispatch(LOGOUT_USER);
  }

  toggleNavigation() {
    if (this.navigationOpen) {
      this.closeNavigation();
    }
    else {
      this.openNavigation();
    }
  }

  openNavigation() {
    this.navigationOpen = true;
  }

  closeNavigation() {
    this.navigationOpen = false;
  }

  toggleCostingMenu() {
    this.costingMenuOpen = !this.costingMenuOpen;
  }

  toggleHeaderMenu() {
    this.headerOpen = !this.headerOpen;
  }

  get translations() {
    return this.localizationState.translations;
  }

  translationOrFallback(key: string, fallback: string) {
    const translation = this.translations.get(key);
    return translation === "---" ? fallback : translation;
  }

  getDefaultTrialUpsellMessage() {
    return this.translationOrFallback("upsell_default_message", "This feature requires a paid subscription to use.");
  }

  formatUpsellAddonMessage(featureName: string, addonName: string) {
    return this.translationOrFallback(
      "upsell_paid_addon_message",
      "'{featureName}' is available through the paid '{addonName}' add-on. Upgrade your subscription to access the module."
    )
      .replace("{featureName}", featureName)
      .replace("{addonName}", addonName);
  }

  getIngredientCostsUpsellMessage() {
    return this.formatUpsellAddonMessage("Ingredient Costs", "Recipe Cost");
  }

  getAdditionalCostsUpsellMessage() {
    return this.formatUpsellAddonMessage("Additional Costs", "Recipe Cost");
  }

  getRecipeCostsUpsellMessage() {
    return this.formatUpsellAddonMessage("Recipe Costs", "Recipe Cost");
  }

  getRecipeInvoiceUpsellMessage() {
    return this.formatUpsellAddonMessage("Recipe Invoice", "Recipe Cost");
  }

  getWellnessUpsellMessage() {
    return this.translationOrFallback(
      "upsell_wellness_message",
      "'Wellness Analyzer' is available as a paid add-on. Upgrade your subscription to access the module."
    );
  }

  getAllergenAiUpsellMessage() {
    return this.translationOrFallback(
      "upsell_allergen_ai_message",
      "'AI Allergen Detector' is available as a paid add-on. Upgrade your subscription to access the module."
    );
  }

  getIngredientCostsTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("ingr-cost-title", "Ingredient Costs"),
      heading: this.translationOrFallback("tutorial_ingredient_costs_heading", "See how Ingredient Costs helps your team update bulk ingredient pricing with less manual work"),
      description: this.translationOrFallback("tutorial_ingredient_costs_description", "Walk through the core ingredient cost flow and preview how pricing updates can be managed directly inside the Recipe Cost add-on."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmnywc8qo29hi1q1eelgpc2cr?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("tutorial_ingredient_costs_embed_title", "Add Bulk Ingredient Cost"),
    };
  }

  getAdditionalCostsTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("tutorial_additional_costs_title", "Additional Costs"),
      heading: this.translationOrFallback("tutorial_additional_costs_heading", "See how Additional Costs lets you capture overhead and supporting charges alongside recipe data"),
      description: this.translationOrFallback("tutorial_additional_costs_description", "Preview how teams can add the extra costs that affect recipe profitability, from packaging to labor and other supporting expenses."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmo02mfgb435t1q1e4yj2ikmu?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("tutorial_additional_costs_embed_title", "Menusano adding additional cost"),
    };
  }

  getRecipeCostsTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("tutorial_recipe_costs_title", "Recipe Costs"),
      heading: this.translationOrFallback("tutorial_recipe_costs_heading", "See how Recipe Costs turns recipe data into a clearer costing workflow for your team"),
      description: this.translationOrFallback("tutorial_recipe_costs_description", "Explore how the Recipe Cost add-on brings ingredient pricing and recipe composition together so you can review recipe-level cost details faster."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmnoxdv237iznaburdt40j2z7?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("tutorial_recipe_costs_embed_title", "Menusano Recipe costing"),
    };
  }

  getRecipeInvoiceTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("tutorial_recipe_invoice_title", "Recipe Invoice"),
      heading: this.translationOrFallback("tutorial_recipe_invoice_heading", "See how Recipe Invoice helps your team connect recipe costing data to invoice-driven workflows"),
      description: this.translationOrFallback("tutorial_recipe_invoice_description", "Preview the invoice flow and how recipe cost data can be organized into a more practical billing and documentation process."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmo0argd70gqs8v9x9sukf76x?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("tutorial_recipe_invoice_embed_title", "Menusano add recipe invoice"),
    };
  }

  getWellnessTutorial(): TutorialConfig {
    return {
      title: this.translationOrFallback("tutorial_wellness_title", "Wellness Analyzer"),
      heading: this.translationOrFallback("tutorial_wellness_heading", "See how the Wellness Analyzer turns calorie data into a guided client-ready experience"),
      description: this.translationOrFallback("tutorial_wellness_description", "Explore the core flow in a few minutes and see how your team can deliver clearer wellness recommendations with less manual effort."),
      subtext: this.translationOrFallback("tutorial_common_subtext", "Click through the steps to explore the workflow in action."),
      embedSrc: "https://app.supademo.com/embed/cmoac5m3f0dywaer7103a0io4?embed_v=2&utm_source=embed",
      embedTitle: this.translationOrFallback("tutorial_wellness_embed_title", "Personalize Wellness Plans with Calorie Analysis"),
    };
  }

  created() {
    let language: string = this.$store.getters.getUserDetails.localization;
    language = language.substring(0, 2);

    if (this.$validator.dictionary.hasLocale(language)) {
      this.$validator.localize(language);
    }
    else {
      import(`vee-validate/dist/locale/${language}`).then((locale) => {
        this.$validator.localize(language, locale);
      });
    }

    this.$store.dispatch(TRANSLATIONS_FETCH, {
      category: "pages",
      collections: ["common"],
    });

    this.trialUpsellMessage = this.getDefaultTrialUpsellMessage();
    this.currentBuildId = window.localStorage.getItem("currentBuildId");
    this.bannerText = window.localStorage.getItem("bannerText") || "";
  }

  @Watch("$route", { immediate: true, deep: true })
  onUrlChange() {
    if (this.isCostingRouteActive) {
      this.costingMenuOpen = true;
    }

    let onbloardFlowLoader = (<any>window).onboardFlowLoader;
    if (onbloardFlowLoader && (<any>window).onboardFlowSettings) {
      onbloardFlowLoader.identify((<any>window).onboardFlowSettings);
    }
  }

  mounted() {
    Vue.nextTick(() => {
      // @ts-ignore
      $.Pages.initSidebar();
    });

    // poll immediately on load
    this.pollVersion();

    // start interval for every 10 minutes
    this.pollTimer = window.setInterval(() => {
      this.pollVersion();
    }, 600000);
  }

  async pollVersion() {
    try {
      const response = await systemRepository.getBuildVersion();
      const nextBuildId = response?.data?.buildId || "";
      const nextMessage = response?.data?.message || "";

      if (!nextBuildId) {
        return;
      }

      if (this.currentBuildId === nextBuildId) {
        return;
      }

      this.currentBuildId = nextBuildId;
      this.bannerText = nextMessage;
      window.localStorage.setItem("currentBuildId", nextBuildId);
      window.localStorage.setItem("bannerText", nextMessage);
    }
    catch (err) {
      console.warn("Version poll failed", err);
    }
  }

  beforeDestroy() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  }
}
export default User;
